/**
 * HealthTile - Combined health tile (Recovery + Sleep + Wellness + Activity + Weight)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon } from '@heroicons/react/24/outline';
import { Panel, Badge, Spinner } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';

interface WellnessAvg {
  avg: number | null;
  latest: number | null;
}

interface HealthTileProps {
  // Recovery
  readinessScore: number | null;
  hrvRmssd: number | null;
  hrvBaseline: number | null;
  restingHr: number | null;
  rhrBaseline: number | null;
  sleepDebtMinutes: number | null;
  // Sleep
  sleepDurationMinutes: number | null;
  sleepEfficiency: number | null;
  sleepScore: number | null;
  remMinutes: number | null;
  deepMinutes: number | null;
  lightMinutes: number | null;
  // Wellness (last 10 aggregate)
  wellness: {
    mood: WellnessAvg;
    energy: WellnessAvg;
    clarity: WellnessAvg;
    irritability: WellnessAvg;
  };
  // Weight
  weightCurrent: number | null;
  weightTarget: number | null;
  weightUnit?: string;
  // Activity
  steps: number | null;
  stepsTarget?: number;
  workoutMinutes: number | null;
  trainingMinutesWeek: number | null;
  // Meta
  loading?: boolean;
}

const WELLNESS_LABELS: Record<string, Record<number, string>> = {
  mood: { 1: 'Bad', 2: 'Okay', 3: 'Good' },
  energy: { 1: 'Low', 2: 'Medium', 3: 'High' },
  clarity: { 1: 'Foggy', 2: 'Clear', 3: 'Sharp' },
  irritability: { 1: 'Irritable', 2: 'Neutral', 3: 'Calm' },
};

const getWellnessVariant = (avg: number | null): 'success' | 'warning' | 'error' | 'default' => {
  if (avg === null) return 'default';
  if (avg >= 2.5) return 'success';
  if (avg >= 1.5) return 'warning';
  return 'error';
};

const getWellnessLabel = (key: string, avg: number): string => {
  const rounded = Math.round(avg);
  const clamped = Math.max(1, Math.min(3, rounded));
  return WELLNESS_LABELS[key][clamped];
};

const dividerStyle: React.CSSProperties = {
  height: '1px',
  backgroundColor: COLORS.bg.hover,
  margin: `${SPACING[8]}px 0`,
};

const labelStyle: React.CSSProperties = {
  fontSize: TYPOGRAPHY.body.sizes.small,
  color: COLORS.text.tertiary,
};

const valueStyle: React.CSSProperties = {
  fontSize: TYPOGRAPHY.body.sizes.default,
  fontWeight: '600',
  color: COLORS.text.primary,
};

const HealthTile: React.FC<HealthTileProps> = ({
  readinessScore, hrvRmssd, hrvBaseline, restingHr, rhrBaseline, sleepDebtMinutes,
  sleepDurationMinutes, sleepEfficiency, sleepScore, remMinutes, deepMinutes, lightMinutes,
  wellness,
  weightCurrent, weightTarget, weightUnit = 'kg',
  steps, stepsTarget = 10000, workoutMinutes, trainingMinutesWeek,
  loading = false,
}) => {
  if (loading) {
    return (
      <Panel style={{ minHeight: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="md" />
      </Panel>
    );
  }

  // Recovery helpers
  const getTrainingState = (score: number | null): { label: string; variant: 'success' | 'warning' | 'error' } => {
    if (score === null) return { label: 'Unknown', variant: 'warning' };
    if (score >= 70) return { label: 'Push', variant: 'success' };
    if (score >= 50) return { label: 'Maintain', variant: 'warning' };
    return { label: 'Deload', variant: 'error' };
  };

  const getHrvDelta = (): string | null => {
    if (hrvRmssd === null || hrvBaseline === null || hrvBaseline === 0) return null;
    const delta = Math.round(((hrvRmssd - hrvBaseline) / hrvBaseline) * 100);
    return `${delta > 0 ? '+' : ''}${delta}%`;
  };

  const getRhrDelta = (): string | null => {
    if (restingHr === null || rhrBaseline === null) return null;
    const delta = Math.round(restingHr - rhrBaseline);
    return `${delta > 0 ? '+' : ''}${delta}`;
  };

  // Sleep helpers
  const formatDuration = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  const getStagePercentages = () => {
    const total = (remMinutes || 0) + (deepMinutes || 0) + (lightMinutes || 0);
    if (total === 0) return null;
    return {
      deep: Math.round(((deepMinutes || 0) / total) * 100),
      rem: Math.round(((remMinutes || 0) / total) * 100),
      light: Math.round(((lightMinutes || 0) / total) * 100),
    };
  };

  // Activity helpers
  const stepsProgress = steps !== null ? Math.min((steps / stepsTarget) * 100, 100) : 0;

  const trainingState = getTrainingState(readinessScore);
  const stages = getStagePercentages();
  const hasRecovery = readinessScore !== null || hrvRmssd !== null || restingHr !== null;
  const hasSleep = sleepDurationMinutes !== null || sleepScore !== null;
  const hasWellness = wellness.mood.avg !== null || wellness.energy.avg !== null || wellness.clarity.avg !== null || wellness.irritability.avg !== null;
  const hasWeight = weightCurrent !== null;
  const hasActivity = steps !== null || workoutMinutes !== null;

  const wellnessMetrics = [
    { label: 'Mood', key: 'mood', data: wellness.mood },
    { label: 'Energy', key: 'energy', data: wellness.energy },
    { label: 'Clarity', key: 'clarity', data: wellness.clarity },
    { label: 'Calm', key: 'irritability', data: wellness.irritability },
  ];

  return (
    <Panel>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[8] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <HeartIcon style={{ width: '18px', height: '18px', color: COLORS.status.error }} />
          <span style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Health
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          {readinessScore !== null && <Badge variant={trainingState.variant} size="sm">{trainingState.label}</Badge>}
          <Link to="/health" style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.accent.default, textDecoration: 'none' }}>
            View All
          </Link>
        </div>
      </div>

      {/* Recovery Section */}
      {hasRecovery && (
        <div style={{ display: 'flex', gap: SPACING[16], flexWrap: 'wrap', alignItems: 'baseline' }}>
          {readinessScore !== null && (
            <div>
              <span style={labelStyle}>Readiness </span>
              <span style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: '700', color: COLORS.text.primary }}>
                {readinessScore}
              </span>
            </div>
          )}
          {hrvRmssd !== null && (
            <div>
              <span style={labelStyle}>HRV </span>
              <span style={valueStyle}>{Math.round(hrvRmssd)}ms</span>
              {getHrvDelta() && (
                <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginLeft: SPACING[4] }}>
                  {getHrvDelta()}
                </span>
              )}
            </div>
          )}
          {restingHr !== null && (
            <div>
              <span style={labelStyle}>RHR </span>
              <span style={valueStyle}>{Math.round(restingHr)}bpm</span>
              {getRhrDelta() && (
                <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginLeft: SPACING[4] }}>
                  {getRhrDelta()}
                </span>
              )}
            </div>
          )}
          {sleepDebtMinutes !== null && sleepDebtMinutes > 30 && (
            <div>
              <span style={labelStyle}>Debt </span>
              <span style={{ ...valueStyle, color: sleepDebtMinutes > 120 ? COLORS.status.warning : COLORS.text.primary }}>
                {Math.floor(sleepDebtMinutes / 60)}h {sleepDebtMinutes % 60}m
              </span>
            </div>
          )}
        </div>
      )}

      {/* Sleep Section */}
      {hasSleep && (
        <>
          <div style={dividerStyle} />
          <div>
            <div style={{ display: 'flex', gap: SPACING[16], alignItems: 'baseline', marginBottom: stages ? SPACING[4] : 0 }}>
              {sleepDurationMinutes !== null && (
                <div>
                  <span style={labelStyle}>Sleep </span>
                  <span style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: '700', color: COLORS.text.primary }}>
                    {formatDuration(sleepDurationMinutes)}
                  </span>
                </div>
              )}
              {sleepScore !== null && (
                <div>
                  <span style={labelStyle}>Score </span>
                  <span style={{ ...valueStyle, color: sleepScore >= 85 ? COLORS.status.success : sleepScore >= 70 ? COLORS.status.warning : COLORS.status.error }}>
                    {Math.round(sleepScore)}
                  </span>
                </div>
              )}
              {sleepEfficiency !== null && (
                <div>
                  <span style={labelStyle}>Efficiency </span>
                  <span style={valueStyle}>{Math.round(sleepEfficiency)}%</span>
                </div>
              )}
            </div>
            {stages && (
              <div>
                <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', backgroundColor: COLORS.bg.hover }}>
                  <div style={{ width: `${stages.deep}%`, backgroundColor: '#8B5CF6' }} />
                  <div style={{ width: `${stages.rem}%`, backgroundColor: COLORS.accent.default }} />
                  <div style={{ width: `${stages.light}%`, backgroundColor: '#93C5FD' }} />
                </div>
                <div style={{ display: 'flex', gap: SPACING[8], marginTop: SPACING[4] }}>
                  <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>Deep {stages.deep}%</span>
                  <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>REM {stages.rem}%</span>
                  <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>Light {stages.light}%</span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Wellness Section (avg of last 10) */}
      {hasWellness && (
        <>
          <div style={dividerStyle} />
          <div style={{ display: 'flex', gap: SPACING[12], flexWrap: 'wrap' }}>
            {wellnessMetrics.map(({ label, key, data }) => data.avg !== null ? (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                <span style={labelStyle}>{label}</span>
                <Badge variant={getWellnessVariant(data.avg)} size="sm">
                  {getWellnessLabel(key, data.avg)}
                </Badge>
                <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
                  {data.avg.toFixed(1)}
                </span>
              </div>
            ) : null)}
            <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary, alignSelf: 'center' }}>
              avg 10
            </span>
          </div>
        </>
      )}

      {/* Weight Section */}
      {hasWeight && (
        <>
          <div style={dividerStyle} />
          <div style={{ display: 'flex', gap: SPACING[16], alignItems: 'baseline' }}>
            <div>
              <span style={labelStyle}>Weight </span>
              <span style={valueStyle}>
                {typeof weightCurrent === 'number' ? Number(weightCurrent).toFixed(1) : weightCurrent} {weightUnit}
              </span>
            </div>
            {weightTarget !== null && (
              <div>
                <span style={labelStyle}>Target </span>
                <span style={valueStyle}>{weightTarget.toFixed(1)} {weightUnit}</span>
              </div>
            )}
            {weightTarget !== null && weightCurrent !== null && (
              <span style={{
                fontSize: TYPOGRAPHY.body.sizes.small,
                color: weightCurrent <= weightTarget ? COLORS.status.success : COLORS.text.secondary,
              }}>
                {weightCurrent <= weightTarget ? 'On target' : `${(Number(weightCurrent) - weightTarget).toFixed(1)} ${weightUnit} to go`}
              </span>
            )}
          </div>
        </>
      )}

      {/* Activity Section */}
      {hasActivity && (
        <>
          <div style={dividerStyle} />
          <div style={{ display: 'flex', gap: SPACING[16], alignItems: 'center', flexWrap: 'wrap' }}>
            {steps !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], flex: '1 1 auto', minWidth: '180px' }}>
                <span style={valueStyle}>{steps.toLocaleString()} steps</span>
                <div style={{ flex: 1, height: '6px', backgroundColor: COLORS.bg.hover, borderRadius: '3px', overflow: 'hidden', maxWidth: '120px' }}>
                  <div style={{
                    width: `${stepsProgress}%`,
                    height: '100%',
                    backgroundColor: stepsProgress >= 100 ? COLORS.status.success : COLORS.accent.default,
                    borderRadius: '3px',
                  }} />
                </div>
                <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary }}>
                  {Math.round(stepsProgress)}%
                </span>
              </div>
            )}
            {workoutMinutes !== null && workoutMinutes > 0 && (
              <div>
                <span style={labelStyle}>Workout </span>
                <span style={valueStyle}>{workoutMinutes}m</span>
              </div>
            )}
            {trainingMinutesWeek !== null && (
              <div>
                <span style={labelStyle}>Week </span>
                <span style={valueStyle}>{Math.round(trainingMinutesWeek / 60)}h</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* No data at all */}
      {!hasRecovery && !hasSleep && !hasWellness && !hasWeight && !hasActivity && (
        <div style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.default, textAlign: 'center', padding: SPACING[8] }}>
          No health data
        </div>
      )}
    </Panel>
  );
};

export default HealthTile;
